/**
 * QaDrawerTab — Registers QA testing as a drawer tab.
 *
 * Only mounts when QA is enabled (localStorage toggle).
 * Shows dot indicator when there are untested items for the current route.
 */

import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { FlaskConical } from 'lucide-react';
import { useDrawer } from '../DrawerSystem';
import { QaSidebar, endpoints, qaHeaders } from '@qa-sidekick/core/client';
import QA_TEST_ITEMS from '../../qa/testItems';

function getTesterId() {
  let id = localStorage.getItem('kuutio-qa-tester-id');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('kuutio-qa-tester-id', id); }
  return id;
}

function currentWeek() {
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

export default function QaDrawerTab() {
  const qaEnabled = localStorage.getItem('kuutio-qa-enabled') === 'true';
  // Avoid calling useDrawer when QA is off — keeps lab pages (e.g. /statmap)
  // free of drawer coupling, and survives HMR context mismatches better.
  if (!qaEnabled) return null;
  return <QaDrawerTabActive />;
}

function QaDrawerTabActive() {
  const { registerTab, unregisterTab } = useDrawer();
  const location = useLocation();
  const route = location.pathname;
  const [dotColor, setDotColor] = useState(null);

  const qaEnabled = true;

  // Check for untested items on current route
  const checkStatus = useCallback(async () => {
    if (!qaEnabled) return;

    const routeItems = QA_TEST_ITEMS.filter(i => i.routes.includes(route));
    if (routeItems.length === 0) { setDotColor(null); return; }

    try {
      const res = await fetch(`${endpoints.results(currentWeek())}?route=${route}`, { headers: qaHeaders() });
      if (!res.ok) { setDotColor('#c084fc'); return; } // purple = untested (can't reach backend)
      const data = await res.json();
      const testerId = getTesterId();
      const hasRed = data.items?.some(i => i.results?.some(r => r.tester_id === testerId && r.status === 'red'));
      const hasYellow = data.items?.some(i => i.results?.some(r => r.tester_id === testerId && r.status === 'yellow'));
      const allTested = data.items?.every(i => i.results?.some(r => r.tester_id === testerId));

      if (hasRed) setDotColor('#ef4444');
      else if (hasYellow) setDotColor('#facc15');
      else if (!allTested) setDotColor('#c084fc');
      else setDotColor(null); // all green, no dot
    } catch {
      setDotColor('#c084fc');
    }
  }, [qaEnabled, route]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  useEffect(() => {
    registerTab({
      id: 'qa',
      label: 'Testaa',
      icon: <FlaskConical size={18} />,
      color: 'rgba(147, 51, 234, 0.3)',
      borderColor: 'rgba(147, 51, 234, 0.5)',
      dot: dotColor,
      order: 10, // after other tabs
      render: () => <QaSidebar embedded items={QA_TEST_ITEMS} />,
    });

    return () => unregisterTab('qa');
  }, [dotColor, registerTab, unregisterTab]);

  return null;
}
